<script setup lang="ts">
  import { Button } from '@/components/ui/button'
  import { inject, ref, computed, type Ref } from 'vue'
  import { Paperclip, Mic, Send, Loader2 } from 'lucide-vue-next'
  import type { AIChatSession } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      YourMessage: 'Your message...',
      Generating: 'Generating...',
      SendMessage: 'Send message',
      AttachFile: 'Attach file',
      RecordVoice: 'Record voice message'
    },
    de: {
      YourMessage: 'Ihre Nachricht...',
      Generating: 'Generiere...',
      SendMessage: 'Nachricht senden',
      AttachFile: 'Datei anhängen',
      RecordVoice: 'Sprachnachricht aufnehmen'
    }
  })

  const sendChatMessage = inject(
    'sendChatMessage',
    async (session: AIChatSession | null, message: string) => {}
  )
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })
  const userChatMessage = ref('')

  const sendMessage = () => {
    if (userChatMessage.value.trim() && !isActiveSessionGenerating.value) {
      sendChatMessage(activeChatSession.value, userChatMessage.value.trim())
      userChatMessage.value = ''
    }
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }
</script>
<template>
  <!-- Message Input -->
  <div class="mt-auto rounded-r-lg border-t border-gray-200 bg-white pb-4 ps-2">
    <textarea
      :disabled="isActiveSessionGenerating"
      class="rounded-0 block max-h-36 w-full resize-none overflow-y-auto border-transparent pb-2 pe-4 ps-2 pt-4
        leading-4 focus:border-transparent focus:outline-none focus:ring-transparent
        disabled:pointer-events-none disabled:opacity-50 md:text-sm
        [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300
        [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar]:w-2"
      :placeholder="t('YourMessage')"
      style="height: 67px"
      v-model="userChatMessage"
      @keydown="handleKeydown"
    ></textarea>

    <div class="flex items-center justify-between gap-x-1 pe-4">
      <!-- Button Group -->
      <div class="invisible flex items-center gap-x-1">
        <!-- Button -->
        <button
          type="button"
          class="flex size-8 items-center justify-center gap-x-1.5 rounded-full text-sm text-gray-600
            hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
            disabled:opacity-50"
        >
          <Paperclip class="size-4 shrink-0" />
          <span class="sr-only">{{ t('AttachFile') }}</span>
        </button>
        <!-- End Button -->
      </div>
      <!-- End Button Group -->

      <!-- Button Group -->
      <div class="flex items-center gap-x-1">
        <!-- Button -->
        <button
          type="button"
          class="invisible flex size-8 items-center justify-center gap-x-1.5 rounded-full text-sm text-gray-600
            hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
            disabled:opacity-50"
        >
          <Mic class="size-4 shrink-0" />
          <span class="sr-only">{{ t('RecordVoice') }}</span>
        </button>
        <!-- End Button -->

        <!-- Send Button -->
        <button
          type="button"
          :disabled="isActiveSessionGenerating || !userChatMessage.trim()"
          @click="sendMessage"
          class="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-600 text-sm
            font-medium text-white hover:bg-purple-700 focus:bg-purple-700 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50"
        >
          <span
            v-if="isActiveSessionGenerating"
            class="animate-spin"
          >
            <Loader2 class="size-4" />
          </span>
          <span v-else>
            <Send class="size-4 shrink-0" />
          </span>
          <span class="sr-only">
            {{ isActiveSessionGenerating ? t('Generating') : t('SendMessage') }}
          </span>
        </button>
        <!-- End Send Button -->
      </div>
      <!-- End Button Group -->
    </div>
  </div>
  <!-- End Message Input -->
</template>
